import { useSyncExternalStore } from 'react';
import { projects } from '@/data/projects';

/**
 * Per-visit project ordering for the journey and the grid.
 *
 * Returns an array of indices into `projects` such that `projects[order[slot]]`
 * is the project rendered at slot `slot`. The shuffle happens once per page
 * load on the client, then is shared across all consumers in the same session
 * — Journey, ScrollProgress sidebar and ProjectFeed all see the same ordering.
 *
 * SSR / hydration model:
 *   • During SSR the `order` ref stays `null` and `getServerJourneyOrder`
 *     returns the canonical exhibitIndex ordering. The server payload is
 *     stable and matches what the client paints on first render — no
 *     hydration mismatch.
 *   • The shuffle fires inside the FIRST `subscribe` call (i.e. the first
 *     `useJourneyOrder()` mount on the client). The new order is stored in
 *     the module-level ref and broadcast via a `queueMicrotask` to all
 *     current subscribers — React re-renders, components paint the
 *     shuffled order.
 *   • SPA navigation between `/` and `/grid` reuses the cached order — both
 *     pages stay in sync within a tab session. A full reload regenerates.
 *
 * JSON-LD on `/grid` is deliberately NOT shuffled: crawlers consume the
 * server payload, where the canonical order reads as an intentional,
 * stable ItemList. The visual shuffle is a UX layer on top of that.
 */

// Fisher-Yates shuffle of [0..n-1]. Unbiased; modern V8 inlines `random()`.
function shuffleIndices(n: number): number[] {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Canonical fallback — matches what the previous static
// `[...projects].sort((a,b) => a.exhibitIndex - b.exhibitIndex)` produced.
// Used on SSR, on the first client paint before the shuffle has fired,
// and inside the `/grid` JSON-LD generation.
//
// Computed ONCE at module load and frozen so `useSyncExternalStore`'s
// snapshot stability check is satisfied. A fresh array per call would
// trip React's "getSnapshot should be cached" warning and risk an
// infinite render loop on the canonical → shuffled transition.
const CANONICAL_ORDER: readonly number[] = Object.freeze(
  [...projects.keys()].sort(
    (a, b) => projects[a].exhibitIndex - projects[b].exhibitIndex,
  ),
);

// Module-level state. `order` is null until the first client-side
// subscription, then it stays put for the lifetime of the tab session.
let order: readonly number[] | null = null;
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((cb) => cb());
}

function ensureGenerated(): void {
  if (order !== null) return;
  // Only generate on the client. Calling shuffleIndices during SSR would
  // burn one shuffle on each request without anyone observing it AND
  // mismatch the next client render.
  if (typeof window === 'undefined') return;
  order = shuffleIndices(projects.length);
  // Deferred so callers that trigger generation inside their own
  // subscribe handler don't observe a synchronous notify during React's
  // commit phase (which throws useSyncExternalStore warnings).
  queueMicrotask(notify);
}

export function getJourneyOrder(): readonly number[] {
  return order ?? CANONICAL_ORDER;
}

export function getServerJourneyOrder(): readonly number[] {
  return CANONICAL_ORDER;
}

export function subscribeJourneyOrder(cb: () => void): () => void {
  ensureGenerated();
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * React hook — returns the per-visit ordering. Re-renders the component
 * once when the order flips from canonical → shuffled (first mount on
 * the client). Stays stable for the rest of the session.
 */
export function useJourneyOrder(): readonly number[] {
  return useSyncExternalStore(
    subscribeJourneyOrder,
    getJourneyOrder,
    getServerJourneyOrder,
  );
}

/**
 * Test-only helper — resets the module-level cache so a fresh shuffle
 * can be observed across test cases. Not exported from a barrel; tests
 * import it directly.
 */
export function __resetJourneyOrderForTests(): void {
  order = null;
  subscribers.clear();
}
